/** Closed catalog of REEL&SHORTS B-ROLL layouts — mirrors backend/reels_shorts_broll.py. */

export const BROLL_SPLIT_HALF = 'split_half';
export const BROLL_SPLIT_HALF_HORIZONTAL = 'split_half_horizontal';
export const BROLL_TRIPLE_HORIZONTAL = 'triple_horizontal';
export const BROLL_CONTEXT_OVERLAY = 'context_overlay';
export const BROLL_SERIAL_CARDS = 'serial_cards';
export const BROLL_CARD_CAROUSEL = 'card_carousel';

export const OVERLAY_POSITIONS = [
  'top',
  'bottom',
  'left',
  'right',
  'center',
  'top_center',
  'bottom_center',
];

export const REELS_SHORTS_BROLL_TYPES = [
  {
    id: BROLL_SPLIT_HALF,
    label: '2 Half Screen (Vertical)',
    layout: 'two_pane_vertical_split',
    description:
      'Left/right split: snippet video on one side, stock video / image / text on the other.',
  },
  {
    id: BROLL_SPLIT_HALF_HORIZONTAL,
    label: '2 Half Screen (Horizontal)',
    layout: 'two_pane_horizontal_split',
    description:
      'Top/bottom split: snippet video in one half, stock video / image / text in the other.',
  },
  {
    id: BROLL_TRIPLE_HORIZONTAL,
    label: '3-Part Horizontal Slice',
    layout: 'three_row_horizontal',
    description:
      'Three horizontal bands — video in the center, text / image / video on top and bottom.',
  },
  {
    id: BROLL_CONTEXT_OVERLAY,
    label: 'Context Overlay',
    layout: 'positioned_overlay',
    description:
      'Icons, emojis, GIFs, cutouts, or cool font stickers timed to the spoken beat — with a transparent cutout or a small tile background.',
    positions: OVERLAY_POSITIONS,
  },
  {
    id: BROLL_SERIAL_CARDS,
    label: 'Serial Cards (Bottom)',
    layout: 'bottom_card_rail',
    description:
      'Cards along the bottom. The matching card highlights and scrolls to center when its narration context plays.',
  },
  {
    id: BROLL_CARD_CAROUSEL,
    label: 'Sliding Card Carousel',
    layout: 'sliding_card_strip',
    description:
      'A row of image cards that slides when the script names the next product, step, or category. Suggested only for list-like speech.',
  },
];

export const REELS_SHORTS_PIPELINE_STEPS = [
  { id: 'upload', label: 'Upload', hint: 'Snippets on S3' },
  { id: 'split', label: 'Split A/V', hint: 'Silent video + audio' },
  { id: 'transcribe', label: 'Transcribe', hint: 'Gemini 3.5 Live' },
  { id: 'editor', label: 'AI editor', hint: 'Order + B-ROLL' },
  { id: 'export', label: 'Final video', hint: 'Burn-in + export' },
];

export function brollTypeMeta(typeId) {
  return REELS_SHORTS_BROLL_TYPES.find((t) => t.id === typeId) || null;
}

export function snippetPipelineStep(snippet) {
  const st = String(snippet?.status || '').toLowerCase();
  if (st === 'uploaded') return 'upload';
  if (st === 'splitting') return 'split';
  if (st === 'transcribing') return 'transcribe';
  if (st === 'failed') return 'split';
  if (st.startsWith('ready')) return 'transcribe';
  return 'upload';
}

export function projectPipelineStep(project, job) {
  if (String(job?.job_type || '') === 'reels_shorts_render') return 'export';
  const st = String(project?.status || job?.status || '').toLowerCase();
  const msg = String(job?.message || '').toLowerCase();
  const rendering =
    msg.includes('render')
    || msg.includes('concatenat')
    || msg.includes('final video')
    || msg.includes('final reel');
  if (rendering) return 'export';
  if (project?.final_video_url && (st === 'completed' || st === 'failed')) return 'export';
  if (st === 'completed') return 'editor';
  if (st === 'awaiting_review') return 'transcribe';
  if (st === 'running' || st === 'pending') {
    if (msg.includes('editor') || msg.includes('chatgpt') || msg.includes('b-roll')) return 'editor';
    if (msg.includes('transcri')) return 'transcribe';
    return 'split';
  }
  if (st === 'failed') return 'split';
  return 'upload';
}

export function orderedSnippets(project) {
  const snippets = Array.isArray(project?.snippets) ? project.snippets : [];
  const order =
    project?.editor_cut?.ordered_snippet_ids
    || project?.edit_plan?.story?.ordered_snippet_ids;
  if (!Array.isArray(order) || !order.length) {
    return [...snippets].sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
  }
  const byId = new Map(snippets.map((s) => [s.id, s]));
  const out = [];
  order.forEach((id) => {
    if (byId.has(id)) out.push(byId.get(id));
  });
  snippets.forEach((s) => {
    if (!order.includes(s.id)) out.push(s);
  });
  return out;
}

export function brollsForSnippet(project, snippetId) {
  const list =
    project?.editor_cut?.broll_suggestions
    || project?.edit_plan?.timeline?.broll;
  if (!Array.isArray(list)) return [];
  return list.filter((row) => row?.snippet_id === snippetId);
}

export function primaryBrollForSnippet(project, snippetId) {
  return brollsForSnippet(project, snippetId)[0] || null;
}

export function positionsForBrollType(typeId) {
  if (typeId === BROLL_SPLIT_HALF) return ['left', 'right'];
  if (typeId === BROLL_SPLIT_HALF_HORIZONTAL) return ['top', 'bottom'];
  if (typeId === BROLL_CONTEXT_OVERLAY) return OVERLAY_POSITIONS;
  return [];
}

/** Companion-pane layouts that occupy screen real estate next to the talking head. */
export const LAYOUT_BROLL_TYPES = [
  BROLL_SPLIT_HALF,
  BROLL_SPLIT_HALF_HORIZONTAL,
  BROLL_TRIPLE_HORIZONTAL,
];

/** Overlays and cards that sit on top of the talking-head frame. */
export const ARTIFACT_BROLL_TYPES = [
  BROLL_CONTEXT_OVERLAY,
  BROLL_SERIAL_CARDS,
  BROLL_CARD_CAROUSEL,
];

export function isLayoutBrollType(typeId) {
  return LAYOUT_BROLL_TYPES.includes(String(typeId || ''));
}

export function isArtifactBrollType(typeId) {
  return ARTIFACT_BROLL_TYPES.includes(String(typeId || ''));
}

export function isSlidingCardCarouselType(typeId) {
  return String(typeId || '') === BROLL_CARD_CAROUSEL;
}

export function isSlidingCardCarouselClip(clip) {
  return isSlidingCardCarouselType(clip?.brollType || clip?.broll_type);
}

export const DEFAULT_BROLL_VISUAL_STYLE = 'photoreal_cinematic';

/** Art looks for generated B-ROLL stills — same keys as narration storyboard styles, minus web scraper. */
export const BROLL_VISUAL_STYLES = [
  { value: 'photoreal_cinematic', label: 'Photoreal / cinematic real', directive: 'Photoreal live-action still, natural skin texture, lens depth of field, practical lighting, film grain.' },
  { value: 'pixar_cinematic', label: 'Pixar / Toy Story', directive: '3D-inspired digital illustration, rounded forms, warm golden-hour light, soft shadows, expressive eyes.' },
  { value: 'anime', label: 'Anime', directive: 'Anime cel-shaded look, crisp ink outlines, flat fills, vivid saturated palette, strong backlight.' },
  { value: 'studio_ghibli', label: 'Studio Ghibli', directive: 'Watercolor-gouache painted look, soft natural light, pastel skies, paper grain, muted earthy palette.' },
  { value: 'hyperrealistic', label: 'Hyperrealistic', directive: 'Ultra-realistic cinematic concept art, volumetric light, high detail, not cartoon or anime.' },
  { value: 'indian_political_satire', label: 'Satirical photoreal (news-comedy look)', directive: 'Semi-photoreal satirical look, slight caricature, punchy contrast, bold silhouettes.' },
  { value: 'classic_disney', label: 'Classic Disney animation', directive: '2D hand-drawn cel animation, warm ink lines, flat fills, storybook primaries.' },
  { value: 'comic_book', label: 'Comic book', directive: 'Heavy inks, halftone dots, high contrast, bold color blocking.' },
  { value: 'manga_seinen', label: 'Manga (seinen)', directive: 'Seinen manga monochrome, fine linework, screentone, high-contrast grayscale.' },
  { value: 'watercolor_storybook', label: 'Watercolor storybook', directive: 'Soft watercolor washes, paper texture, pastel palette, wet-on-wet edges.' },
  { value: 'digital_painting', label: 'Digital painting', directive: 'Painterly digital illustration, visible brushstrokes, cinematic rim light.' },
  { value: 'claymation', label: 'Claymation / stop-motion', directive: 'Clay stop-motion look, matte sculpted surfaces, warm tungsten light.' },
  { value: 'oil_painting', label: 'Classical oil painting', directive: 'Oil-on-canvas look, impasto strokes, chiaroscuro, earth-tone palette.' },
  { value: 'noir_film', label: 'Film noir', directive: 'Noir monochrome still, high contrast, hard key light, film grain.' },
  { value: 'cyberpunk_neon', label: 'Cyberpunk neon', directive: 'Neon-noir sci-fi, magenta-cyan rim light, wet speculars, dark night palette.' },
  { value: 'minimalist_flat', label: 'Minimalist flat', directive: 'Flat vector illustration, hard edges, limited palette, bold silhouettes.' },
  { value: 'dreamlike_surreal', label: 'Dreamlike surreal', directive: 'Surrealist painterly look, ethereal glow, pastel-iridescent gradients.' },
  { value: 'retro_80s_cartoon', label: 'Retro 80s cartoon', directive: '1980s cel cartoon, bold outlines, flat saturated primaries, slight VHS softness.' },
];

const BROLL_STYLE_ALIASES = {
  photoreal: 'photoreal_cinematic',
  photorealistic: 'photoreal_cinematic',
  pixar: 'pixar_cinematic',
  ghibli: 'studio_ghibli',
  hyper_real: 'hyperrealistic',
  'hyper-real': 'hyperrealistic',
  cyber_synth: 'cyberpunk_neon',
  noir_sketch: 'noir_film',
};

export function brollVisualStyleMeta(styleId) {
  const key = sanitizeBrollVisualStyle(styleId);
  return BROLL_VISUAL_STYLES.find((row) => row.value === key) || BROLL_VISUAL_STYLES[0] || null;
}

export function sanitizeBrollVisualStyle(value, fallback = DEFAULT_BROLL_VISUAL_STYLE) {
  const raw = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  const mapped = BROLL_STYLE_ALIASES[raw] || raw;
  if (BROLL_VISUAL_STYLES.some((row) => row.value === mapped)) return mapped;
  const fb = String(fallback || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  const mappedFb = BROLL_STYLE_ALIASES[fb] || fb;
  if (BROLL_VISUAL_STYLES.some((row) => row.value === mappedFb)) return mappedFb;
  return DEFAULT_BROLL_VISUAL_STYLE;
}

export function projectBrollVisualStyle(project) {
  return sanitizeBrollVisualStyle(
    project?.edit_plan?.story?.visual_style
      || project?.editor_cut?.broll_visual_style
      || project?.input?.broll_visual_style,
  );
}
